<template>
  <u-container>
    <div>
      <section class="relative">
        <x-glow :type="1" />
        <div class="max-w-6xl mx-auto px-4 sm:px-6">
          <div class="pt-32 md:pt-40">
            <div class="text-center pb-12">
              <h1 class="h1 bg-clip-text text-transparent bg-linear-to-r from-slate-200/60 via-slate-200 to-slate-200/60 pb-4">YouTube Thumbnail Downloader</h1>
              <div class="max-w-3xl mx-auto">
                <p class="text-lg text-slate-400">Paste a YouTube video <span class="font-bold">URL</span> or <span class="font-bold">ID</span> below and we will fetch all available thumbnail images.</p>
              </div>
              <div v-if="error.exists == false">
                <div>
                  <div class="mt-10">
                    <input
                      id="url-id-input"
                      v-model="input"
                      @keyup.enter="() => submit(input)"
                      type="text"
                      :class="{ 'x-disabled': disabled }"
                      autocomplete="on"
                      class="x-form-input md:max-w-1/2 w-full"
                      placeholder="YouTube video URL or ID"
                      aria-label="YouTube video URL or ID"
                    />
                  </div>
                  <div>
                    <button id="submit-button" class="mt-8 x-button" :class="{ 'x-disabled': disabled }" @click="() => submit(input)">
                      <span v-if="loading">Loading...</span>
                      <span v-else>Submit</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loading">
          <div class="text-center">
            <div class="animate-spin inline-block size-6 border-3 border-current border-t-transparent text-slate-50 rounded-full" role="status" aria-label="loading">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <div v-else-if="error.exists">
          <section class="relative mx-auto">
            <div class="max-w-6xl mx-auto px-4 sm:px-6">
              <div class="relative px-8 py-12 md:py-20 [border-image:linear-gradient(to_right,transparent,var(--color-slate-800),transparent)1]">
                <x-glow :type="2" />
                <div class="max-w-3xl mx-auto text-center">
                  <div>
                    <div class="inline-flex font-medium bg-clip-text text-transparent bg-linear-to-r from-purple-500 to-purple-200 pb-3">Error</div>
                  </div>
                  <h2 class="h2 bg-clip-text text-transparent bg-linear-to-r from-slate-200/60 via-slate-200 to-slate-200/60 pb-4">{{ error.code }}</h2>
                  <p class="text-lg text-slate-400 mb-8">
                    <span>{{ error.message }}</span>
                  </p>
                  <div>
                    <button class="x-button w-1/2" @click="reset">Reset</button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div v-else-if="success" id="thumbnail-container">
          <div :class="`${style.border} mb-10`"></div>

          <div class="flex flex-col items-center justify-center text-center pb-10">
            <p class="font-bold h3 text-slate-50 pb-3">
              <span>
                {{ information.title }}
              </span>
            </p>
            <div class="inline-flex mb-2 text-slate-50/50">BY</div>
            <div class="text-sm font-medium text-slate-50 mb-2">
              {{ information.creator }}
            </div>
            <div>
              <a class="text-purple-500 hover:underline" :href="information.url" target="_blank">{{ information.url }}</a>
            </div>
          </div>

          <div :class="`${style.border} mb-10`"></div>

          <aside v-if="state.hd_missing" class="pl-6 border-l-2 border-red-600 mb-5">
            <footer class="flex items-center space-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
                <path d="M12 9h.01" />
                <path d="M11 12h1v4h1" />
              </svg>
              <div class="text-sm font-medium text-slate-300"><span class="font-bold mr-1">HD</span> thumbnail is not available.</div>
            </footer>
          </aside>

          <div v-if="thumbnails.length">
            <div v-for="thumbnail in thumbnails">
              <div v-if="thumbnail.image.found" class="grid md:grid-cols-4 gap-4 mt-5 mb-30 thumbnail-list-item">
                <figure class="md:col-span-2 bg-linear-to-b from-slate-300/20 to-transparent rounded-3xl p-px">
                  <img class="w-full rounded-[inherit]" :src="thumbnail.image.src" />
                </figure>
                <div class="md:col-span-2 md:ml-5 mt-1">
                  <div class="flex flex-row">
                    <div class="md:h2 h4 p-2 rounded-md slate-200 uppercase mr-5 text-slate-50" :class="`${thumbnail.resolution.style}`">{{ thumbnail.resolution.text }}</div>
                    <h1 class="self-center basis-full md:h2 h4 bg-clip-text text-transparent bg-linear-to-r from-slate-200/60 via-slate-200 to-slate-200/60">{{ thumbnail.label }}</h1>
                  </div>
                  <div>
                    <button id="thumbnail-download-button" @click="save(thumbnail)" class="x-clickable mt-8 px-4 py-2 rounded-3xl text-slate-50 flex content-center x-clickable" :class="`${thumbnail.resolution.style}`">
                      <div class="mr-1">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                          <path d="M7 11l5 5l5 -5" />
                          <path d="M12 4l0 12" />
                        </svg>
                      </div>
                      <div class="font-bold mr-1">{{ thumbnail.resolution.text }}</div>
                      <div>Download</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </u-container>
</template>

<script lang="ts">
import axios from "axios";
import { saveAs } from "file-saver";
import { type AxiosError, type AxiosRequestConfig } from "axios";
import sanitize from "sanitize-filename";
import { fileTypeFromBuffer } from "file-type";

import NotFound from "~/assets/apps/youtube/not-found.jpg";

import * as _enum from "~/enums";
import { useSimpleSeo, DEFAULT_SEO_KEYWORDS } from "#imports";

const keywords = [...DEFAULT_SEO_KEYWORDS, "youtube", "youtube downloader", "youtube thumbnail downloader", "downloader", "thumbnail"];

namespace youtube {
  export type thumbnails = Awaited<ReturnType<typeof fetchThumbnails>>;
  export type information = Awaited<ReturnType<typeof fetchVideoInformation>>;
  export namespace error {
    export enum code {
      _0 = 0,
      _500 = 500,
      _404 = 404,
      _403 = 403
    }
    export interface scheme {
      exists: boolean;
      message: string;
      code: error.code;
    }
  }

  export enum format {
    MAX_RES_DEFAULT = "maxresdefault.jpg",
    SD_DEFAULT = "sddefault.jpg",
    HQ_DEFAULT = "hqdefault.jpg",
    MQ_DEFAULT = "mqdefault.jpg",
    DEFAULT = "default.jpg"
  }

  export interface global {
    image: {
      loading: boolean;
      object: HTMLImageElement;
      dimensions: { width: number; height: number };
    };
  }
}

class YoutubeError extends Error {
  public error: Error | null;
  public code: youtube.error.code;

  constructor(message: string, code: youtube.error.code, error: Error | null = null) {
    super(message);
    this.name = "YoutubeError";
    this.code = code;
    this.error = error;

    if (this.error) {
      console.error(this.error);
    }
  }
}

const _error = reactive<youtube.error.scheme>({
  exists: false,
  message: "",
  code: youtube.error.code._0
});

const _state = reactive({
  hd_missing: false
});

// No HD Image tWn_UmUx_Uk

/**
 * Example
 *
 * Video - yhuBAZnhv_Q
 * Short - TNXH9Efoo00
 */

interface Oembed {
  title: string;
  author_name: string;
  author_url: string;
  type: "video";
  height: number;
  width: number;
  version: string;
  provider_name: "YouTube";
  provider_url: "https://www.youtube.com/";
  thumbnail_height: number;
  thumbnail_width: number;
  thumbnail_url: string;
  html: string;
}

/**
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 *
 * NOTE: Should work if `v=` is not the first query parameter
 */
const YOUTUBE_VIDEO_URL_PATTERN = /(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/))([A-Za-z0-9_-]{11})/i;

var global = null as null | youtube.global;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchVideoInformation(id: string) {
  try {
    const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`;

    const response = await axios.get<Oembed>(url, {
      responseType: "json"
    });

    const video = response.data;

    return {
      title: video.title,
      url: video.author_url,
      creator: video.author_name
    };
  } catch (error: unknown) {
    const e = error as AxiosError;

    if (e.status) {
      switch (e.status) {
        case 400:
          throw new YoutubeError("Video Not Found", youtube.error.code._404);
          break;
        case 403:
          throw new YoutubeError("Video Is Private", youtube.error.code._403);
          break;
      }
    }

    console.error("Failed to fetch video information:", error);

    throw error;
  }
}

async function fetchThumbnailImage(url: string) {
  let found = false;
  let response = null;

  const config = { responseType: "arraybuffer" } as AxiosRequestConfig;

  try {
    response = await axios.get(url, config);
    found = true;
  } catch (error: unknown) {
    let abort = true;
    const e = error as AxiosError;

    if (e.status) {
      if (e.status === 404) {
        response = await axios.get(NotFound, config);

        if (response.status === 200) {
          abort = false;
        }
      }
    }

    if (abort) {
      console.error("Failed to fetch image:", error);

      throw error;
    }
  }

  if (response == null) {
    throw new Error("Failed to get response");
  }

  try {
    // response.data will be an ArrayBuffer in the browser
    const buffer = response.data;

    // You can now create a Blob and a URL to display the image
    const blob = new Blob([buffer]);
    const src = URL.createObjectURL(blob);

    if (global == null) {
      throw new YoutubeError("Error 334. Something went wrong.", youtube.error.code._500);
    }

    global.image.loading = true;

    global.image.object.src = src;

    let attempts = 0;

    while (global.image.loading) {
      if (attempts >= 100) {
        throw new Error("Failed to load image resolutions");
      }

      await sleep(100);

      attempts++;
    }

    const dimensions = JSON.parse(JSON.stringify(global.image.dimensions));

    return {
      src: src,
      blob: blob,
      found: found,
      buffer: buffer,
      dimensions: dimensions
    };
  } catch (error) {
    console.error("Failed to fetch image:", error);

    throw error;
  }
}

function getVideoIdFromInput(input: string) {
  if (input == "") {
    return false;
  }

  /**
   * Trim whitespace and remove surrounding angle brackets or spaces
   */
  input = input.trim();

  /**
   * (1) Try Regex
   */
  const match = input.match(YOUTUBE_VIDEO_URL_PATTERN);

  if (match && match[1]) {
    return match[1];
  }

  /**
   * (2) Fallback: try parsing URL and grabbing `v` query param (covers edge cases)
   */
  try {
    const url = new URL(input.startsWith("http") ? input : `https://${input}`);

    const query = { v: url.searchParams.get("v") };

    if (query.v && /^[A-Za-z0-9_-]{11}$/.test(query.v)) {
      return query.v;
    }
  } catch (e) {
    /**
     * Not a full URL — continue to next fallback
     */
  }

  /**
   * (3) Fallback: if user pasted only an ID
   */
  if (/^[A-Za-z0-9_-]{11}$/.test(input)) {
    return input;
  }

  return false;
}

/**
 * Extract the YouTube video ID from either a plain ID or a URL.
 * Returns false when the input does not contain a valid ID.
 */

/**
 * Generate an array of thumbnail objects for a given video ID.
 * Each object contains the URL and a descriptive label corresponding
 * to the variant. We include the common variants documented by
 * YouTube.
 */

async function fetchThumbnails(id: string) {
  const formats = [
    /**
     * Thumbnail
     */
    { format: youtube.format.MAX_RES_DEFAULT },

    /**
     * Main
     */
    { format: youtube.format.SD_DEFAULT },
    { format: youtube.format.HQ_DEFAULT },
    { format: youtube.format.MQ_DEFAULT },
    { format: youtube.format.DEFAULT }

    // /**
    //  * Others
    //  */
    // { format: "0.jpg" },
    // { format: "1.jpg" },
    // { format: "2.jpg" },
    // { format: "3.jpg" }
  ];

  const thumbnails = [];

  for (var i = 0; i < formats.length; i++) {
    let value = formats[i];

    if (value) {
      let url = `https://img.youtube.com/vi/${id}/${value.format}`;

      let image = await fetchThumbnailImage(url);

      let resolution = {
        text: "LQ",
        style: "bg-gradient-to-r from-slate-900 to-slate-700"
      };

      switch (true) {
        case image.dimensions.width >= 720:
          resolution.text = "HD";
          resolution.style = "bg-gradient-to-r from-red-500 to-orange-500";
          break;
        case image.dimensions.width < 720 && image.dimensions.width >= 480:
          resolution.text = "SD";
          resolution.style = "bg-gradient-to-r from-blue-800 to-indigo-900";
          break;
      }

      let label = `Thumbnail - (${image.dimensions.width} x ${image.dimensions.height})`;

      thumbnails.push({
        url: url,
        image: image,
        label: label,
        resolution: resolution
      });

      if (image.found == false) {
        if (value.format === youtube.format.MAX_RES_DEFAULT) {
          _state.hd_missing = true;
        }
      }

      if (image.found) {
        break;
      }
    }
  }

  return thumbnails;
}

function updatePageIdQuery(id: string) {
  const route = useRoute();
  const router = useRouter();

  router.replace({ query: { ...route.query, id: id } });
}

function getPageIdQuery() {
  const route = useRoute();
  const query = route.query;

  if (query) {
    let id = query.id;

    if (Array.isArray(id)) {
      id = id[0];
    }

    return id;
  }

  return false;
}

export default {
  setup() {
    useSimpleSeo({
      title: "YouTube Thumbnail Downloader - Incigma",
      description: "Download high-res YouTube thumbnails instantly. Paste a URL to save original or max-resolution images. Free, fast, privacy-first.",
      keywords: keywords,
      author: "Incigma.com",
      image: _enum.og.app_youtube_thumbnail_downloader
    });
  },
  data() {
    return {
      id: "",
      loading: false,
      success: false,
      input: "" as any,
      state: _state,
      error: _error,
      style: {
        border: "border-t [border-image:linear-gradient(to_right,transparent,var(--color-slate-600),transparent)1]"
      },
      thumbnails: [] as youtube.thumbnails,
      information: { title: "", creator: "", url: "" } as youtube.information
    };
  },
  async mounted() {
    if (import.meta.client) {
      global = {
        image: {
          loading: true,
          object: new Image(),
          dimensions: { width: 0, height: 0 }
        }
      };

      global.image.object.onload = function () {
        const image = this as HTMLImageElement;

        if (global == null) {
          throw new YoutubeError("Error 558. Something went wrong.", youtube.error.code._500);
        }

        global.image.dimensions.height = image.naturalHeight;
        global.image.dimensions.width = image.naturalWidth;

        global.image.loading = false;
        global.image.object.src = "";
      };
    }

    const id = getPageIdQuery();

    if (id) {
      this.input = id;
      await this.submit(id);
    }
  },
  computed: {
    disabled() {
      return this.loading || this.error.exists;
    }
  },
  methods: {
    async submit(input: string) {
      if (this.loading) {
        return;
      }

      try {
        this.reset();

        this.loading = true;

        const id = getVideoIdFromInput(input);

        if (id === false) {
          throw new YoutubeError("Invalid video ID or URL", youtube.error.code._500);
        }

        this.id = id;

        updatePageIdQuery(id);

        this.information = await fetchVideoInformation(id);

        this.thumbnails = await fetchThumbnails(id);

        this.success = true;
        this.loading = false;

        useSimpleSeo({
          title: `${this.information.title} - YouTube Thumbnail Downloader`,
          description: "Download high-res YouTube thumbnails instantly. Paste a URL to save original or max-resolution images. Free, fast, privacy-first.",
          keywords: keywords,
          author: this.information.creator,
          image: _enum.og.app_youtube_thumbnail_downloader
        });
      } catch (error: any) {
        switch (true) {
          case error instanceof YoutubeError:
            _error.exists = true;
            _error.code = error.code;
            _error.message = error.message;
            this.loading = false;
            break;
          default:
            throw new YoutubeError("Something went wrong.", youtube.error.code._500, error);
        }
      }
    },
    async save(thumbnail: youtube.thumbnails[0]) {
      let filename = `${this.information.creator} - ${this.information.title} ${thumbnail.label} [${this.id}]`;

      filename = sanitize(filename);

      const fileType = await fileTypeFromBuffer(thumbnail.image.buffer);

      filename += `.${fileType?.ext}`;

      saveAs(thumbnail.image.blob, filename);
    },
    reset() {
      this.loading = false;
      this.success = false;
      _error.exists = false;
      _state.hd_missing = false;
    }
  }
};
</script>
